import React, { useState } from 'react';
import { Landmark, FileText, Rocket, CheckCircle, DollarSign, Clock, Ban, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/TestAuthContext';
import axios from '@/lib/http';
import { FinancialValue } from '@/components/shared/FinancialValue';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface PolicyData {
  policy_info: {
    policy_number: string | null;
    carrier: string | null;
    effective_date: string | null;
    expiration_date: string | null;
    policy_type: string | null;
  };
  coverage: {
    business_interruption: boolean;
    extended_business_income: boolean;
    extra_expense: boolean;
    civil_authority: boolean;
  };
  limits: {
    aggregate_limit: number | null;
    per_occurrence_limit: number | null;
    time_limit_months: number | null;
  };
  sublimits: {
    extended_period_indemnity_days: number | null;
    extra_expense_limit: number | null;
    civil_authority_limit: number | null;
  };
  deductibles: {
    time_deductible_hours: number | null;
    time_deductible_days: number | null;
    monetary_deductible: number | null;
  };
  exclusions: Array<{
    type: string;
    description: string;
    severity: string;
    applies_to_bi: boolean;
  }>;
  warnings: string[];
  confidence: number;
}

interface PolicyUploadProps {
  reportId?: string;
  onPolicyUploaded?: (data: PolicyData) => void;
  onClose?: () => void;
}

export default function PolicyUpload({ reportId, onPolicyUploaded, onClose }: PolicyUploadProps) {
  const { getToken } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [policyData, setPolicyData] = useState<PolicyData | null>(null);
  const [error, setError] = useState('');

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.name.toLowerCase().endsWith('.pdf')) {
        setSelectedFile(file);
        setError('');
      } else {
        setError('Please select a PDF file');
      }
    }
  };

  const uploadPolicy = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', selectedFile);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent: any) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      };

      const url = reportId 
        ? `${BACKEND_URL}/api/v1/policy/upload?report_id=${reportId}`
        : `${BACKEND_URL}/api/v1/policy/upload`;

      const response = await axios.post(url, formData, config);

      setPolicyData(response.data.policy_data);
      setIsUploading(false);
      
      if (onPolicyUploaded) {
        onPolicyUploaded(response.data.policy_data);
      }
    } catch (err: any) {
      console.error('Policy upload failed:', err);
      setError(err.response?.data?.detail || 'Upload failed. Please try again.');
      setIsUploading(false);
    }
  };

  return (
    <div className="rounded-xl border-2 p-6 shadow-lg" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Landmark size={28} style={{ color: 'var(--color-text-secondary)' }} />
          <div>
            <h3 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Policy Upload</h3>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>We'll read your policy for you</p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            style={{ color: 'var(--color-text-dim)' }}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {!policyData ? (
        <>
          {/* Upload Area */}
          <div className="border-2 border-dashed rounded-lg p-8 text-center mb-4" style={{ borderColor: 'var(--color-border)' }}>
            {selectedFile ? (
              <div>
                <FileText size={36} className="mx-auto mb-2" style={{ color: 'var(--color-text-secondary)' }} />
                <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{selectedFile.name}</p>
                <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>
            ) : (
              <div>
                <Landmark size={36} className="mx-auto mb-2" style={{ color: 'var(--color-text-secondary)' }} />
                <p className="font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Upload Policy PDF</p>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  We'll extract limits, deductibles, and exclusions
                </p>
              </div>
            )}
          </div>

          <input
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            className="hidden"
            id="policy-file-input"
          />

          {!selectedFile && (
            <label
              htmlFor="policy-file-input"
              className="block w-full py-3 rounded-lg font-semibold text-center cursor-pointer transition"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              Choose Policy PDF
            </label>
          )}

          {selectedFile && !isUploading && (
            <button
              onClick={uploadPolicy}
              className="w-full py-3 rounded-lg font-bold transition inline-flex items-center justify-center gap-2"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              <Rocket size={16} /> Parse Policy
            </button>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Parsing policy...</span>
                <span className="font-bold" style={{ color: 'var(--color-needs-human-text)' }}>{uploadProgress}%</span>
              </div>
              <div className="w-full rounded-full h-3" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${uploadProgress}%`, backgroundColor: 'var(--color-brand)' }}
                />
              </div>
              <p className="text-xs text-center" style={{ color: 'var(--color-text-secondary)' }}>
                Extracting limits, deductibles, and exclusions...
              </p>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 border rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
              <p className="text-sm flex items-center gap-1" style={{ color: 'var(--color-needs-human-text)' }}><AlertTriangle size={14} /> {error}</p>
            </div>
          )}
        </>
      ) : (
        /* Results View */
        <div className="space-y-4">
          {/* Success Banner */}
          <div className="border rounded-lg p-4" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}>
            <div className="flex items-center gap-3">
              <CheckCircle size={24} style={{ color: 'var(--color-verified)' }} />
              <div>
                <p className="font-bold" style={{ color: 'var(--color-verified)' }}>Policy Parsed Successfully</p>
                <p className="text-sm" style={{ color: 'var(--color-verified)' }}>
                  Confidence: {policyData.confidence}%
                </p>
              </div>
            </div>
          </div>

          {/* Coverage Limits */}
          {policyData.limits.aggregate_limit && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
              <h4 className="font-bold mb-2 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <DollarSign size={16} /> Coverage Limits
              </h4>
              <div className="space-y-2">
                {policyData.limits.aggregate_limit && (
                  <div className="flex justify-between text-sm">
                    <span style={{ color: 'var(--color-text-secondary)' }}>Aggregate Limit:</span>
                    <span className="font-bold" style={{ color: 'var(--color-text-primary)' }}>
                      <FinancialValue value={policyData.limits.aggregate_limit} />
                    </span>
                  </div>
                )}
                {policyData.limits.per_occurrence_limit && (
                  <div className="flex justify-between text-sm">
                    <span style={{ color: 'var(--color-text-secondary)' }}>Per Occurrence:</span>
                    <span className="font-bold" style={{ color: 'var(--color-text-primary)' }}>
                      <FinancialValue value={policyData.limits.per_occurrence_limit} />
                    </span>
                  </div>
                )}
                {policyData.limits.time_limit_months && (
                  <div className="flex justify-between text-sm">
                    <span style={{ color: 'var(--color-text-secondary)' }}>Time Limit:</span>
                    <span className="font-bold" style={{ color: 'var(--color-text-primary)' }}>
                      {policyData.limits.time_limit_months} months
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Deductibles */}
          {(policyData.deductibles.time_deductible_days || policyData.deductibles.monetary_deductible) && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
              <h4 className="font-bold mb-2 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <Clock size={16} /> Deductibles
              </h4>
              <div className="space-y-2">
                {policyData.deductibles.time_deductible_days && (
                  <div className="flex justify-between text-sm">
                    <span style={{ color: 'var(--color-text-secondary)' }}>Waiting Period:</span>
                    <span className="font-bold" style={{ color: 'var(--color-text-primary)' }}>
                      {policyData.deductibles.time_deductible_days} days
                    </span>
                  </div>
                )}
                {policyData.deductibles.monetary_deductible && (
                  <div className="flex justify-between text-sm">
                    <span style={{ color: 'var(--color-text-secondary)' }}>Monetary Deductible:</span>
                    <span className="font-bold" style={{ color: 'var(--color-text-primary)' }}>
                      <FinancialValue value={policyData.deductibles.monetary_deductible} />
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Exclusions */}
          {policyData.exclusions && policyData.exclusions.length > 0 && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
              <h4 className="font-bold mb-2 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <Ban size={16} /> Exclusions
              </h4>
              <div className="space-y-2">
                {policyData.exclusions.map((exclusion, idx) => (
                  <div key={idx} className="text-sm">
                    <span className="font-bold" style={{
                      color: exclusion.severity === 'HIGH' ? 'var(--color-needs-human-text)' :
                      exclusion.severity === 'MEDIUM' ? 'var(--color-brand)' :
                      'var(--color-text-secondary)'
                    }}>
                      {exclusion.type}
                    </span>
                    {exclusion.applies_to_bi && (
                      <span className="ml-2 text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
                        Applies to BI
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warnings */}
          {policyData.warnings && policyData.warnings.length > 0 && (
            <div className="border rounded-lg p-3" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
              <p className="text-xs font-semibold mb-1 flex items-center gap-1" style={{ color: 'var(--color-brand)' }}><AlertTriangle size={12} /> Warnings:</p>
              <ul className="text-xs space-y-1" style={{ color: 'var(--color-brand)' }}>
                {policyData.warnings.map((warning, idx) => (
                  <li key={idx}>• {warning}</li>
                ))}
              </ul>
            </div>
          )}

          <button
            onClick={onClose}
            className="w-full py-3 rounded-lg font-semibold transition"
            style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
